import { TextInput, type TextInputProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

/**
 * The grid search box: keystrokes stay in this component's own state, so typing re-renders only
 * the toolbar, never the page around it. `value` is the URL's own current search (`?q=`) and
 * `onChange` patches it — but only once, 300ms after the last keystroke. The row query then reads
 * that same URL value directly (`StudiosPage`/`GamesPage` no longer debounce it a second time —
 * that used to add this component's 300ms to another 300ms, so the grid updated ~600ms after the
 * last keystroke instead of ~300ms). An external change to `value` (Clear filters, back/forward, a
 * link that arrives with `?q=` set) still takes the box over immediately.
 *
 * `lastWritten` is what tells an ECHO of this component's own write apart from a genuinely
 * external change: `onChange` patches the URL, which re-renders this component with `value`
 * equal to what it just wrote. Without the check, that echo's `useEffect(() =>
 * setText(value), [value])` would stomp any keystroke typed in the gap between the write and
 * that re-render arriving, and it would just as happily replay a pending debounce onto a
 * `value` that Clear filters had already reset — clicking Clear while a debounce is in flight
 * used to leave the typed text on screen and write it back 300ms later. Now an external change
 * both takes the box over immediately AND cancels any pending write (`cancelDebounce`), and an
 * echo of this component's own write is a no-op — `text` (which may already be further ahead)
 * is left alone.
 *
 * Accepted gap: if this component unmounts (navigating away) within 300ms of the last keystroke,
 * the pending write is dropped — the URL keeps whatever it last held, not the unsubmitted text.
 */
export const DebouncedSearchInput = ({
    value,
    onChange,
    ...rest
}: { value: string; onChange: (value: string) => void } & Omit<TextInputProps, 'value' | 'onChange'>) => {
    const [text, setText] = useState(value);
    const [debounced, cancelDebounce] = useDebouncedValue(text, 300);
    const lastWritten = useRef(value);

    useEffect(() => {
        if (value === lastWritten.current) return;
        lastWritten.current = value;
        setText(value);
        cancelDebounce();
    }, [value, cancelDebounce]);

    useEffect(() => {
        if (debounced !== value) {
            lastWritten.current = debounced;
            onChange(debounced);
        }
        // `value`/`onChange` intentionally excluded: only the debounce elapsing should trigger a
        // write, never a re-render carrying the same debounced text with a new `onChange` identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    return (
        <TextInput
            leftSection={<IconSearch size={14} stroke={1.7} />}
            value={text}
            onChange={(event) => setText(event.currentTarget.value)}
            {...rest}
        />
    );
};
